interface SparklineProps {
  values: number[];
  /** Accessible summary, e.g. "Registrations over the last 7 days". */
  label: string;
  width?: number | undefined;
  height?: number | undefined;
}

/**
 * A seven-point trend line.
 *
 * Inline SVG rather than a charting library: this draws one polyline, and the
 * smallest credible chart dependency is tens of kilobytes for a shape that is
 * four lines of arithmetic. The dashboard renders two of these and a set of
 * KPI tiles — nothing here needs axes, tooltips or a legend.
 */
export function Sparkline({ values, label, width = 96, height = 24 }: SparklineProps) {
  if (values.length < 2) return null;

  const max = Math.max(...values);
  const min = Math.min(...values);
  // A flat series would divide by zero; drawing it through the middle is
  // honest — the point of a flat line is that nothing changed.
  const span = max - min || 1;

  const stepX = width / (values.length - 1);
  const points = values
    .map((value, index) => {
      const x = index * stepX;
      // SVG y grows downward, so the value is inverted.
      const y = height - ((value - min) / span) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const lastValue = values.at(-1) ?? 0;
  const lastX = (values.length - 1) * stepX;
  const lastY = height - ((lastValue - min) / span) * height;

  return (
    <svg
      className="c-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={`${label}: ${values.join(', ')}`}
      // Lets the line scale with the tile without distorting its stroke.
      preserveAspectRatio="none"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        // preserveAspectRatio="none" would otherwise stretch the stroke too.
        vectorEffect="non-scaling-stroke"
      />
      {/* Marks where the series ends, so "today" is findable at a glance. */}
      <circle cx={lastX} cy={lastY} r="2" fill="currentColor" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
